import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar as CalendarIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { formatDateRange } from '../../utils/dateUtils';
import './CalendarDateRangePicker.css';

export interface CalendarDateRangePickerProps {
  startDate: string;
  endDate: string;
  onChange: (range: { start: string; end: string }) => void;
  minDate?: string;
  maxDate?: string;
  placeholder?: string;
  id?: string;
  className?: string;
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS_HEADER = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];

function toDate(iso: string): Date | null {
  if (!iso) return null;
  const d = new Date(iso + 'T12:00:00');
  return isNaN(d.getTime()) ? null : d;
}

function toISO(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDay(a: Date | null, b: Date | null): boolean {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(date: Date, start: Date | null, end: Date | null): boolean {
  if (!start || !end) return false;
  const t = date.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

type CalendarCell = { date: Date | null; isCurrentMonth: boolean; isEmpty?: boolean };

function getCalendarGrid(year: number, month: number, fillNextMonth = false): CalendarCell[] {
  const first = new Date(year, month, 1);
  const startDow = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate();

  const grid: CalendarCell[] = [];
  for (let i = 0; i < startDow; i++) {
    const d = daysInPrevMonth - startDow + 1 + i;
    grid.push({ date: new Date(prevYear, prevMonth, d), isCurrentMonth: false });
  }
  for (let d = 1; d <= daysInMonth; d++) {
    grid.push({ date: new Date(year, month, d), isCurrentMonth: true });
  }
  const remaining = 42 - grid.length;
  if (fillNextMonth) {
    for (let d = 1; d <= remaining; d++) {
      grid.push({ date: new Date(year, month + 1, d), isCurrentMonth: false });
    }
  } else {
    for (let i = 0; i < remaining; i++) {
      grid.push({ date: null, isCurrentMonth: false, isEmpty: true });
    }
  }
  return grid;
}

export const CalendarDateRangePicker: React.FC<CalendarDateRangePickerProps> = ({
  startDate,
  endDate,
  onChange,
  minDate,
  maxDate,
  placeholder = 'Select date range',
  id,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [selecting, setSelecting] = useState<'start' | 'end'>('start');
  const [draftStart, setDraftStart] = useState<string | null>(null);
  const [draftEnd, setDraftEnd] = useState<string | null>(null);
  const [leftYear, setLeftYear] = useState(() => (startDate ? toDate(startDate)?.getFullYear() : new Date().getFullYear()) ?? new Date().getFullYear());
  const [leftMonth, setLeftMonth] = useState(() => (startDate ? toDate(startDate)?.getMonth() : new Date().getMonth()) ?? new Date().getMonth());
  const [popoverStyle, setPopoverStyle] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const displayStart = open ? (draftStart ?? startDate) : startDate;
  const displayEnd = open ? (draftEnd ?? endDate) : endDate;
  const calendarStart = toDate(displayStart);
  const calendarEnd = toDate(displayEnd);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    setPopoverStyle({
      top: rect.bottom + 8,
      left: rect.left + rect.width / 2,
    });
  }, [open]);

  useEffect(() => {
    if (open) {
      setSelecting('start');
      setDraftStart(startDate);
      setDraftEnd(endDate);
    }
  }, [open, startDate, endDate]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const displayText = startDate && endDate ? formatDateRange(startDate, endDate) : placeholder;

  const rightYear = leftMonth === 11 ? leftYear + 1 : leftYear;
  const rightMonth = leftMonth === 11 ? 0 : leftMonth + 1;

  const goLeftPrev = () => {
    if (leftMonth === 0) {
      setLeftMonth(11);
      setLeftYear((y) => y - 1);
    } else {
      setLeftMonth((m) => m - 1);
    }
  };

  const goLeftNext = () => {
    if (leftMonth === 11) {
      setLeftMonth(0);
      setLeftYear((y) => y + 1);
    } else {
      setLeftMonth((m) => m + 1);
    }
  };

  const isDisabled = (d: Date): boolean => {
    const iso = toISO(d);
    if (minDate && iso < minDate) return true;
    if (maxDate && iso > maxDate) return true;
    return false;
  };

  const handleSelectDate = (d: Date) => {
    const iso = toISO(d);
    if (selecting === 'start') {
      setDraftStart(iso);
      setDraftEnd(iso);
      setSelecting('end');
    } else {
      const from = draftStart ?? startDate;
      const fromDate = toDate(from);
      const clickedDate = toDate(iso);
      const finalStart = fromDate && clickedDate && clickedDate < fromDate ? iso : from;
      const finalEnd = fromDate && clickedDate && clickedDate < fromDate ? from : iso;
      onChange({ start: finalStart, end: finalEnd });
      setOpen(false);
      setSelecting('start');
    }
  };

  const leftGrid = getCalendarGrid(leftYear, leftMonth, false);
  const rightGrid = getCalendarGrid(rightYear, rightMonth, false);

  const renderCalendar = (
    year: number,
    month: number,
    grid: CalendarCell[],
    side: 'left' | 'right'
  ) => (
    <div className="calendar-range-calendar">
      <div className="calendar-range-calendar-header">
        <span className="calendar-range-calendar-month-year">
          {MONTHS[month]} {year}
        </span>
      </div>
      <div className="calendar-range-days-header">
        {DAYS_HEADER.map((day) => (
          <span key={day} className="calendar-range-day-name">{day}</span>
        ))}
      </div>
      <div className="calendar-range-grid">
        {grid.map(({ date, isCurrentMonth, isEmpty }, i) => {
          if (isEmpty || date === null) {
            return <span key={i} className="calendar-range-cell calendar-range-cell-empty" aria-hidden />;
          }
          const selectedStart = isSameDay(date, calendarStart);
          const selectedEnd = isSameDay(date, calendarEnd);
          const inRange = isInRange(date, calendarStart, calendarEnd) && !selectedStart && !selectedEnd;
          const isTodayCell = isSameDay(date, today);
          const disabled = isDisabled(date);
          return (
            <button
              key={i}
              type="button"
              className={`calendar-range-cell ${!isCurrentMonth ? 'other-month' : ''} ${selectedStart ? 'selected start' : ''} ${selectedEnd ? 'selected end' : ''} ${inRange ? 'in-range' : ''} ${isTodayCell ? 'today' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => !disabled && handleSelectDate(date)}
              disabled={disabled}
              aria-label={date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className={`calendar-date-range-picker ${className}`.trim()} ref={containerRef}>
      <button
        type="button"
        id={id}
        className="calendar-date-range-picker-trigger"
        onClick={() => {
          setOpen((o) => {
            if (!o) setSelecting('start');
            return !o;
          });
        }}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={displayText}
      >
        <CalendarIcon size={18} strokeWidth={2} className="calendar-date-range-picker-trigger-icon" aria-hidden />
        <span className="calendar-date-range-picker-trigger-text">{displayText}</span>
      </button>

      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="calendar-date-range-picker-popover"
            role="dialog"
            aria-label="Choose date range"
            style={{
              position: 'fixed',
              top: popoverStyle.top,
              left: popoverStyle.left,
              transform: 'translateX(-50%)',
            }}
          >
            <div className="calendar-date-range-picker-nav">
              <button type="button" className="calendar-date-range-picker-nav-btn" onClick={goLeftPrev} aria-label="Previous month">
                <ChevronLeft size={18} strokeWidth={2} />
              </button>
              <span className="calendar-date-range-picker-nav-label">
                {selecting === 'start' ? 'Select start date' : 'Select end date'}
              </span>
              <button type="button" className="calendar-date-range-picker-nav-btn" onClick={goLeftNext} aria-label="Next month">
                <ChevronRight size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="calendar-date-range-picker-calendars">
              {renderCalendar(leftYear, leftMonth, leftGrid, 'left')}
              {renderCalendar(rightYear, rightMonth, rightGrid, 'right')}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};
